import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { FaCrown, FaWallet, FaHistory, FaCheckCircle, FaTimesCircle, FaCalendarCheck } from 'react-icons/fa';
import { getRequest, postRequest } from '../../services/api/apiService';

const Billing = () => {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupCurrency, setTopupCurrency] = useState('eur');
  const [loading, setLoading] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true' && params.get('topup') === 'true') {
      toast.success('Wallet rechargé avec succès !');
    } else if (params.get('success') === 'true') {
      toast.success('Abonnement Premium activé !');
    } else if (params.get('cancelled') === 'true') {
      toast.info('Paiement annulé.');
    }

    fetchStatus();
    fetchHistory();
  }, []);

  const fetchStatus = async () => {
    try {
      const data = await getRequest('/api/payment/status');
      setStatus(data);
    } catch {
      setStatus({ plan: 'free', isPremium: false, walletBalance: '0.00' });
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await getRequest('/api/payment/history');
      setHistory(res.data || []);
    } catch {
      setHistory([]);
    }
  };

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const { url } = await postRequest('/api/payment/create-subscription');
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur lors de la redirection.');
      setLoading(false);
    }
  };

  const handleTopup = async () => {
    const amount = parseFloat(topupAmount);
    if (!amount || amount < 5) {
      toast.error('Montant minimum : 5 €');
      return;
    }
    setLoading(true);
    try {
      const { url } = await postRequest('/api/payment/create-topup', {
        amount,
        currency: topupCurrency,
      });
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur lors de la recharge.');
      setLoading(false);
    }
  };

  const handlePortal = async () => {
    setLoadingPortal(true);
    try {
      const { url } = await postRequest('/api/payment/portal');
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Aucun abonnement actif.');
      setLoadingPortal(false);
    }
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });

  // Group subscription payments by month for the membership summary
  const membershipByMonth = (() => {
    const rows = [];
    const map = {};
    history
      .filter((p) => p.type === 'subscription' && p.status === 'succeeded')
      .forEach((p) => {
        const d = new Date(p.created_at);
        const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
        const sortKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (!map[label]) {
          map[label] = { label, sortKey, amount: 0, currency: p.currency || 'eur' };
          rows.push(map[label]);
        }
        map[label].amount += parseFloat(p.amount);
      });
    return rows.sort((a, b) => (a.sortKey < b.sortKey ? 1 : -1));
  })();

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <h2 className="text-2xl font-semibold text-[#221C51] mb-6">Facturation & Budget</h2>

      {/* ── Plan actuel ─────────────────────────────────────────────── */}
      <div className="border border-gray-200 rounded-xl p-5 mb-5 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <FaCrown className={status?.isPremium ? 'text-yellow-500 text-xl' : 'text-gray-400 text-xl'} />
          <h3 className="text-lg font-semibold text-[#221C51]">Plan actuel</h3>
          <span className={`ml-auto text-xs font-bold px-3 py-1 rounded-full ${
            status?.isPremium
              ? 'bg-[#221C51] text-white'
              : 'bg-gray-100 text-gray-600'
          }`}>
            {status?.isPremium ? 'PREMIUM' : 'FREE'}
          </span>
        </div>

        {!status?.isPremium ? (
          <div>
            <p className="text-sm text-gray-500 mb-4">
              Passez à Premium pour accéder à toutes les plateformes, créer des campagnes illimitées et utiliser l'agent IA.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 text-sm">
              {[
                'Toutes les plateformes connectées',
                'Campagnes illimitées',
                'Règles automatiques illimitées',
                'Budget publicitaire (wallet)',
                'Agent IA pour créer des campagnes',
                'Historique complet',
              ].map((f) => (
                <div key={f} className="flex items-center gap-2 text-gray-600">
                  <FaCheckCircle className="text-green-500 flex-shrink-0" />
                  <span>{f}</span>
                </div>
              ))}
            </div>
            <button
              onClick={handleUpgrade}
              disabled={loading}
              className="w-full py-3 bg-[#221C51] text-white rounded-xl font-semibold text-sm hover:bg-[#2d267a] disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {loading ? 'Redirection...' : 'Passer à Premium — 49 €/mois'}
            </button>
            <p className="text-xs text-gray-400 text-center mt-2">
              Paiement sécurisé par Stripe · Carte bancaire ou Revolut Pay
            </p>
          </div>
        ) : (
          <div>
            <p className="text-sm text-green-600 font-medium mb-3">
              ✅ Votre abonnement Premium est actif.
            </p>
            <button
              onClick={handlePortal}
              disabled={loadingPortal}
              className="px-5 py-2 border-2 border-[#221C51] text-[#221C51] rounded-xl font-semibold text-sm hover:bg-[#221C51] hover:text-white disabled:opacity-50 transition"
            >
              {loadingPortal ? 'Redirection...' : 'Gérer mon abonnement'}
            </button>
            <p className="text-xs text-gray-400 mt-2">
              Modifier la carte, télécharger les factures, annuler.
            </p>
          </div>
        )}
      </div>

      {/* ── Wallet Budget Publicitaire ───────────────────────────────── */}
      <div className="border border-gray-200 rounded-xl p-5 mb-5 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <FaWallet className="text-[#221C51] text-xl" />
          <h3 className="text-lg font-semibold text-[#221C51]">Budget Publicitaire</h3>
        </div>

        <div className="flex items-baseline gap-2 mb-4">
          <span className="text-4xl font-black text-[#221C51]">
            {status?.walletBalance ?? '0.00'}
          </span>
          <span className="text-lg text-gray-500 font-medium">€</span>
          <span className="text-xs text-gray-400 ml-1">solde disponible</span>
        </div>

        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-[#221C51] mb-1">Montant</label>
            <input
              type="number"
              min="5"
              placeholder="Ex : 100"
              value={topupAmount}
              onChange={(e) => setTopupAmount(e.target.value)}
              className="w-full p-2 text-sm border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:border-[#221C51]"
            />
          </div>
          <div className="w-28">
            <label className="block text-xs font-semibold text-[#221C51] mb-1">Devise</label>
            <select
              value={topupCurrency}
              onChange={(e) => setTopupCurrency(e.target.value)}
              className="w-full p-2 text-sm border border-gray-300 rounded-lg bg-gray-50"
            >
              <option value="eur">EUR €</option>
              <option value="usd">USD $</option>
              <option value="gbp">GBP £</option>
            </select>
          </div>
          <button
            onClick={handleTopup}
            disabled={loading}
            className="px-5 py-2 bg-[#221C51] text-white rounded-xl font-semibold text-sm hover:bg-[#2d267a] disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {loading ? '...' : 'Recharger'}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Minimum 5 € · Paiement par carte ou Revolut Pay · Le solde sera crédité immédiatement après paiement.
        </p>
      </div>

      {/* ── Membership mensuel ──────────────────────────────────────── */}
      <div className="border border-gray-200 rounded-xl p-5 mb-5 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <FaCalendarCheck className="text-[#221C51] text-xl" />
          <h3 className="text-lg font-semibold text-[#221C51]">Abonnement — Mois par mois</h3>
        </div>

        {membershipByMonth.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">Aucun paiement d'abonnement enregistré.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400 border-b">
                <th className="pb-2">Mois</th>
                <th className="pb-2 text-right">Montant réglé</th>
                <th className="pb-2 text-right">Statut</th>
              </tr>
            </thead>
            <tbody>
              {membershipByMonth.map((row) => (
                <tr key={row.sortKey} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-2.5 font-medium text-[#221C51] capitalize">{row.label}</td>
                  <td className="py-2.5 text-right font-semibold text-[#221C51]">
                    {row.amount.toFixed(2)} {row.currency.toUpperCase()}
                  </td>
                  <td className="py-2.5 text-right">
                    <span className="inline-flex items-center gap-1 text-green-600 text-xs font-semibold">
                      <FaCheckCircle /> Réglé
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200">
                <td className="pt-3 text-xs font-bold text-gray-500 uppercase">Total</td>
                <td className="pt-3 text-right font-black text-[#221C51]">
                  {membershipByMonth.reduce((s, r) => s + r.amount, 0).toFixed(2)}{' '}
                  {(membershipByMonth[0]?.currency || 'eur').toUpperCase()}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* ── Historique des paiements ─────────────────────────────────── */}
      <div className="border border-gray-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <FaHistory className="text-[#221C51] text-xl" />
          <h3 className="text-lg font-semibold text-[#221C51]">Historique des paiements</h3>
        </div>

        {history.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">Aucun paiement pour le moment.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400 border-b">
                <th className="pb-2">Date</th>
                <th className="pb-2">Type</th>
                <th className="pb-2">Montant</th>
                <th className="pb-2">Statut</th>
              </tr>
            </thead>
            <tbody>
              {history.map((p) => (
                <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-2 text-gray-500">{formatDate(p.created_at)}</td>
                  <td className="py-2 font-medium text-[#221C51]">
                    {p.type === 'subscription' ? '🔐 Abonnement' : '💰 Recharge wallet'}
                  </td>
                  <td className="py-2 font-semibold">
                    {parseFloat(p.amount).toFixed(2)} {(p.currency || 'eur').toUpperCase()}
                  </td>
                  <td className="py-2">
                    {p.status === 'succeeded' ? (
                      <span className="flex items-center gap-1 text-green-600 font-medium">
                        <FaCheckCircle /> Réussi
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-500 font-medium">
                        <FaTimesCircle /> Échoué
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ToastContainer />
    </div>
  );
};

export default Billing;
